import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

export const DEFAULT_COUNTRY_ISO = 'BR';
export const MAX_E164_DIGITS = 15;

const regionNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });

export function normalizePhoneSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function countryFlagEmoji(iso) {
  return String(iso || '')
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function buildCountryOption(iso) {
  const ddi = getCountryCallingCode(iso);
  const name = regionNames.of(iso) || iso;
  const example = getExampleNumber(iso, examples);

  return {
    iso,
    ddi,
    name,
    flag: countryFlagEmoji(iso),
    placeholder: example?.formatNational() || 'Número de telefone',
    searchText: normalizePhoneSearch(`${iso} ${name} ${ddi}`),
  };
}

export const PHONE_COUNTRIES = getCountries()
  .map(buildCountryOption)
  .sort((a, b) => {
    if (a.iso === DEFAULT_COUNTRY_ISO) return -1;
    if (b.iso === DEFAULT_COUNTRY_ISO) return 1;
    return a.name.localeCompare(b.name, 'pt-BR');
  });

export function getPhoneCountry(iso = DEFAULT_COUNTRY_ISO) {
  return PHONE_COUNTRIES.find((country) => country.iso === iso) || null;
}

export function getDefaultPhoneCountry() {
  return getPhoneCountry(DEFAULT_COUNTRY_ISO) || PHONE_COUNTRIES[0];
}

export function createEmptyPhoneModel(countryIso = DEFAULT_COUNTRY_ISO) {
  return {
    displayValue: '',
    canonicalValue: '',
    countryIso: getPhoneCountry(countryIso)?.iso || DEFAULT_COUNTRY_ISO,
    isPossible: false,
    isValid: false,
    error: null,
  };
}

function resolveCountryIso(phone, preferredCountryIso) {
  if (phone?.country) return phone.country;
  if (
    preferredCountryIso &&
    getPhoneCountry(preferredCountryIso)?.ddi === phone?.countryCallingCode
  ) {
    return preferredCountryIso;
  }
  return PHONE_COUNTRIES.find((country) => country.ddi === phone?.countryCallingCode)?.iso
    || preferredCountryIso
    || DEFAULT_COUNTRY_ISO;
}

function normalizeInternationalPrefix(value) {
  const trimmed = String(value || '').trim();
  return trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;
}

function phoneResult(phone, displayValue, fallbackCountryIso) {
  const canonicalValue = phone?.number?.replace(/^\+/, '') || '';
  const tooLong = canonicalValue.length > MAX_E164_DIGITS;

  return {
    displayValue,
    canonicalValue: tooLong ? '' : canonicalValue,
    countryIso: phone?.country || fallbackCountryIso || DEFAULT_COUNTRY_ISO,
    isPossible: !tooLong && Boolean(phone?.isPossible()),
    isValid: !tooLong && Boolean(phone?.isValid()),
    error: tooLong ? 'too_long' : null,
  };
}

export function parsePhoneInput(rawValue, countryIso = DEFAULT_COUNTRY_ISO) {
  const raw = String(rawValue || '');
  if (!raw.trim()) return createEmptyPhoneModel(countryIso);

  const normalized = normalizeInternationalPrefix(raw);
  const isInternational = normalized.startsWith('+');
  const formatter = new AsYouType(isInternational ? undefined : countryIso);
  const formatted = formatter.input(normalized);
  if (normalized.replace(/\D/g, '').length > MAX_E164_DIGITS) {
    return {
      ...createEmptyPhoneModel(countryIso),
      displayValue: formatted || raw,
      error: 'too_long',
    };
  }
  const phone = parsePhoneNumberFromString(normalized, isInternational ? undefined : countryIso);

  if (!phone) {
    return {
      ...createEmptyPhoneModel(countryIso),
      displayValue: formatted || raw,
      error: 'unparseable',
    };
  }

  const detectedCountry = resolveCountryIso(phone, countryIso);
  const displayValue = isInternational && phone.country ? phone.formatNational() : formatted;
  return phoneResult(phone, displayValue, detectedCountry);
}

export function parseCanonicalPhone(apiValue) {
  const original = String(apiValue || '').trim();
  const digits = original.replace(/\D/g, '');
  if (!digits || digits.length > MAX_E164_DIGITS) return null;

  const phone = parsePhoneNumberFromString(`+${digits}`);
  if (!phone) return null;

  return {
    ...phoneResult(phone, phone.formatNational(), resolveCountryIso(phone)),
    internationalDisplayValue: phone.formatInternational(),
  };
}

export function phoneModelFromApiValue(apiValue, fallbackCountryIso = DEFAULT_COUNTRY_ISO) {
  if (apiValue === null || apiValue === undefined || String(apiValue).trim() === '') {
    return createEmptyPhoneModel(fallbackCountryIso);
  }
  return parseCanonicalPhone(apiValue) || {
    ...createEmptyPhoneModel(fallbackCountryIso),
    displayValue: String(apiValue),
    error: 'unparseable',
  };
}

export function formatPhoneForDisplay(apiValue) {
  const parsed = parseCanonicalPhone(apiValue);
  return parsed?.isPossible ? parsed.internationalDisplayValue : String(apiValue || '');
}

export function toPhonePayload(field, phoneModel) {
  return {
    [field]: phoneModel?.canonicalValue || '',
    pais: phoneModel?.countryIso || DEFAULT_COUNTRY_ISO,
  };
}

// Compatibilidade temporária para consumidores ainda não migrados.
export function formatPhoneForCountry(value, country) {
  return parsePhoneInput(value, country?.iso || DEFAULT_COUNTRY_ISO).displayValue;
}

export function formatLimitedPhoneForCountry(value, country) {
  return formatPhoneForCountry(value, country);
}

export function isPossiblePhoneForCountry(value, country) {
  return parsePhoneInput(value, country?.iso || DEFAULT_COUNTRY_ISO).isPossible;
}
